import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import type {
    Action,
    ActionRevision,
    ActionWithRevisions,
    CreateActionParams,
    UpdateActionParams,
    DeleteActionParams,
    FetchActionsParams,
    CreateRevisionParams,
    UpdateRevisionParams,
    DeleteRevisionParams,
    FetchActionRevisionsParams,
    Execution,
    CreateExecutionParams,
    UpdateExecutionParams,
    FetchExecutionsParams,
    CallbackEventParams,
} from './types/actions';
import { CallbackEventType } from './types/actions';
import { JWTHttpClient } from '@/services/JWTHttpClient';

// Get the actions service URL from environment variable
const ACTIONS_SERVICE_URL =
    import.meta.env.VITE_ACTIONS_SERVICE_URL || 'https://api.stage-ovationcxm.app/actions';

// Create JWT HTTP client for Actions Service API calls
const jwtHttpClient = new JWTHttpClient(ACTIONS_SERVICE_URL);

export enum ActionCategory {
    FORM = 'FORM',
    AI = 'AI',
}

/** Object types for execution filtering */
export enum ObjectType {
    CASE = 'case',
    LOCATION = 'location'
}

/**
 * Composable that provides access to Actions API functions.
 * This safely initializes the httpClient with authStore only when called
 * inside components or other composables after Pinia is ready.
 */
export function useActionsAPI() {
    const authStore = useAuthStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchActions(params: FetchActionsParams = {}): Promise<Action[]> {
            try {

                const queryParams: Record<string, string> = {};

                if (params.orgId) {
                    queryParams.orgId = params.orgId;
                }

                if (params.namespace) {
                    queryParams.namespace = params.namespace;
                }

                if (params.start !== undefined) {
                    queryParams.start = String(params.start);
                }

                if (params.limit !== undefined) {
                    queryParams.limit = String(params.limit);
                }

                const response = await jwtHttpClient.get<Action[]>('v1/actions', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                return response;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching actions:', error);
                throw new Error('Failed to fetch actions');
            }
        },

        async getActionById(id: string): Promise<Action> {
            try {

                const response = await jwtHttpClient.get<Action>(`v1/actions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Action not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action by ID:', error);
                throw error;
            }
        },

        async createAction(params: CreateActionParams): Promise<Action> {
            try {

                const response = await jwtHttpClient.post<Action>('v1/actions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Failed to create action');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating action:', error);
                throw error;
            }
        },

        async updateAction(params: UpdateActionParams): Promise<Action> {
            try {
                const { id, ...body } = params;

                const response = await jwtHttpClient.put<Action>(`v1/actions/${id}`, body, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Failed to update action');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating action:', error);
                throw error;
            }
        },

        async deleteAction(params: DeleteActionParams): Promise<boolean> {
            try {

                await jwtHttpClient.delete<any>(`v1/actions/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                return true;
            } catch (error) {
                console.error('❌ ActionsAPI: Error deleting action:', error);
                throw error;
            }
        },

        // Revision methods
        async fetchActionRevisions(params: FetchActionRevisionsParams): Promise<ActionRevision[]> {
            try {
                const queryParams: Record<string, string> = {};

                if (params.start !== undefined) {
                    queryParams.start = String(params.start);
                }

                if (params.limit !== undefined) {
                    queryParams.limit = String(params.limit);
                }

                const response = await jwtHttpClient.get<ActionRevision[]>(
                    `v1/actions/${params.actionId}/revisions`,
                    Object.keys(queryParams).length > 0 ? queryParams : undefined,
                    {
                        'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                    }
                );
                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch action revisions');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action revisions:', error);
                throw error;
            }
        },

        async getRevisionById(id: string): Promise<ActionRevision> {
            try {

                const response = await jwtHttpClient.get<ActionRevision>(`v1/actions/-/revisions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Revision not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching revision by ID:', error);
                throw error;
            }
        },

        async createRevision(params: CreateRevisionParams): Promise<ActionRevision> {
            try {

                const response = await jwtHttpClient.post<ActionRevision>('v1/actions/-/revisions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Failed to create revision');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating revision:', error);
                throw error;
            }
        },

        async updateRevision(params: UpdateRevisionParams): Promise<ActionRevision> {
            try {
                const { id, ...body } = params;

                const response = await jwtHttpClient.put<ActionRevision>(
                    `v1/actions/-/revisions/${id}`,
                    body,
                    undefined,
                    {
                        'content-type': 'application/json',
                        'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                    }
                );
                if (response) {
                    return response;
                }

                throw new Error('Failed to update revision');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating revision:', error);
                throw error;
            }
        },

        async deleteRevision(params: DeleteRevisionParams): Promise<boolean> {
            try {

                await jwtHttpClient.delete<any>(`v1/actions/-/revisions/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                return true;
            } catch (error) {
                console.error('❌ ActionsAPI: Error deleting revision:', error);
                throw error;
            }
        },

        async getActionWithRevisions(actionId: string): Promise<ActionWithRevisions> {
            try {

                // Fetch the action
                const action = await this.getActionById(actionId);

                // Fetch the revisions
                const revisions = await this.fetchActionRevisions({ actionId });

                // Get the most recent published revision
                const currentRevision = revisions.length > 0 ? revisions[0] : undefined;

                const actionWithRevisions: ActionWithRevisions = {
                    ...action,
                    revisions,
                    currentRevision,
                };
                return actionWithRevisions;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching action with revisions:', error);
                throw error;
            }
        },

        // Execution methods
        async fetchExecutions(params: FetchExecutionsParams = {}): Promise<Execution[]> {
            try {

                const queryParams: Record<string, string> = {};

                if (params.orgId) {
                    queryParams.orgId = params.orgId;
                }

                if (params.object) {
                    queryParams.object = params.object;
                }

                if (params.objectId) {
                    queryParams.objectId = params.objectId;
                }

                if (params.actionId) {
                    queryParams.actionId = params.actionId;
                }

                if (params.revisionId) {
                    queryParams.revisionId = params.revisionId;
                }

                if (params.start !== undefined) {
                    queryParams.start = String(params.start);
                }

                if (params.limit !== undefined) {
                    queryParams.limit = String(params.limit);
                }

                const response = await jwtHttpClient.get<Execution[]>('v1/executions', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                return response;
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching executions:', error);
                throw new Error('Failed to fetch executions');
            }
        },

        async getExecutionById(id: string): Promise<Execution> {
            try {

                const response = await jwtHttpClient.get<Execution>(`v1/executions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Execution not found');
            } catch (error) {
                console.error('❌ ActionsAPI: Error fetching execution by ID:', error);
                throw error;
            }
        },

        async createExecution(params: CreateExecutionParams): Promise<Execution> {
            try {

                const response = await jwtHttpClient.post<Execution>('v1/executions', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Failed to create execution');
            } catch (error) {
                console.error('❌ ActionsAPI: Error creating execution:', error);
                throw error;
            }
        },

        async updateExecution(params: UpdateExecutionParams): Promise<Execution> {
            try {

                const response = await jwtHttpClient.put<Execution>(`v1/executions/${params.id}`, params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                });
                if (response) {
                    return response;
                }

                throw new Error('Failed to update execution');
            } catch (error) {
                console.error('❌ ActionsAPI: Error updating execution:', error);
                throw error;
            }
        },

        /**
         * Cancel an execution by sending a CANCEL event to the callback controller.
         * This method first fetches the execution to get its callbackId, then sends
         * a CANCEL event to the callback endpoint.
         * @param executionId - The ID of the execution to cancel
         * @returns The updated execution with CANCELLED status
         */
        async cancelExecution(executionId: string): Promise<Execution> {
            try {

                // First, fetch the execution to get the callbackId
                const execution = await this.getExecutionById(executionId);

                if (!execution.callbackId) {
                    throw new Error('Execution does not have a callbackId - cannot cancel');
                }

                // Send CANCEL event to the callback controller
                const callbackEvent: CallbackEventParams = {
                    type: CallbackEventType.UPDATE,
                    status: 'CANCELLED',
                };

                const response = await jwtHttpClient.post<Execution>(
                    `v1/callback/${execution.callbackId}`,
                    callbackEvent,
                    undefined,
                    {
                        'content-type': 'application/json',
                        'x-ovationcxm-source': 'com.ovationcxm.actions/ui:actions',
                    }
                );
                if (response) {
                    return response;
                }

                throw new Error('Failed to cancel execution');
            } catch (error) {
                console.error('❌ ActionsAPI: Error cancelling execution:', error);
                throw error;
            }
        },
    };
}
